<script setup lang="ts">
import { debounce } from 'es-toolkit/compat'
import type { OfferTermsContent } from '@promotion/types'
import { computed, onMounted, ref, toValue, useEventListener, usePromotionConfig, whenever, vInternalLinks } from '#imports'

export interface StickyConfig {
    container?: Element | string
    minHeight?: number
    offset?: number
    backgroundColor?: string
}

export interface PromotionTermsProps {
    terms: OfferTermsContent[]
    type?: 'short' | 'long'
    textColor?: string
    showGamblingAuthorityImage?: boolean
    /**
     * Note: Default behavior is to show title if there are multiple terms
     * This option will force show title even if there is only one term
     */
    showTitle?: boolean
    title?: string
    subtle?: boolean
    /**
     * Configure position: sticky support.
     * Properties: `container` (Element or CSS selector for sticky calculation, e.g. `.dialog__body`),
     * `minHeight` (minimum visible height), `offset` (bottom offset for absolute positioned elements).
     */
    stickyConfig?: StickyConfig
}

const props = defineProps<PromotionTermsProps>()

const { gamblingAuthorityImage, gamblingAuthorityUrl } = usePromotionConfig()

const container = ref<Element>()
const element = ref<HTMLDivElement>()
const stickyOffset = ref<string>()

const showTermTitle = computed(() => props.showTitle || props.terms.length > 1)
const getTermTitle = (termsObj: OfferTermsContent) => termsObj.label || props.title

const preparedStickyConfig = computed(() => {
    const { minHeight = 40, offset = 70 } = props.stickyConfig || {}

    return { container: container.value, minHeight, offset }
})

function setContainer() {
    if (import.meta.server || !props.stickyConfig?.container) return
    let containerElement

    if (typeof props.stickyConfig.container === 'string') {
        containerElement = document.querySelector(props.stickyConfig.container)
    }
    else {
        containerElement = props.stickyConfig.container as Element | undefined
    }

    container.value = containerElement || undefined
}

function setStickyOffset() {
    const containerRaw = toValue(container)
    const elementRaw = toValue(element)

    if (!containerRaw || !elementRaw) return

    const { offset } = preparedStickyConfig.value

    const height = elementRaw.clientHeight
    stickyOffset.value = `${-height + offset}px`
}

useEventListener('resize', debounce(setStickyOffset, 50), {
    passive: true
})

onMounted(() => {
    if (props.stickyConfig) {
        setContainer()
        setStickyOffset()
    }
})

whenever(() => props.stickyConfig?.container, () => {
    setContainer()
    setStickyOffset()
}, {
    immediate: true
})
</script>

<template>
    <div
        v-if="terms"
        ref="element"
        class="offer-terms"
        :class="{
            'offer-terms--long': type === 'long',
            'offer-terms--subtle': subtle,
            'offer-terms--sticky': !!stickyConfig
        }"
        :style="{
            'bottom': !!stickyConfig && stickyOffset ? stickyOffset : undefined,
            '--sticky-terms-color': textColor,
            '--sticky-terms-background': stickyConfig?.backgroundColor
        }"
    >
        <a
            v-if="showGamblingAuthorityImage"
            :href="gamblingAuthorityUrl"
            target="_blank"
            rel="noopener noreferrer"
        >
            <NuxtImg
                v-if="gamblingAuthorityImage && gamblingAuthorityUrl"
                class="offer-terms__logo"
                :src="gamblingAuthorityUrl"
                alt=""
                :width="gamblingAuthorityImage.width"
                :height="gamblingAuthorityImage.height"
                preset="local"
                provider="cloudflare"
                loading="lazy"
                decoding="async"
            />
        </a>

        <div v-internal-links class="offer-terms__text">
            <div v-for="(termsObj, index) in terms" :key="index" class="offer-terms__text-wrap">
                <b v-if="(showTermTitle || termsObj.label) && getTermTitle(termsObj)" class="offer-terms__title">{{ getTermTitle(termsObj) }}:</b>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div v-html="termsObj.text" />
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.offer-terms {
    display: flex;
    align-items: center;
    font-size: $font-size-xs;
    transition:
        bottom 0.3s ease-out,
        background 0.2s ease-out;
    color: var(--sticky-terms-color, var(--on-background));

    &__logo {
        margin-right: 5px;
    }

    &__text {
        display: flex;
        gap: 15px;
        line-height: 1.1em;

        :deep(a) {
            text-decoration: underline;
            white-space: nowrap;
            color: var(--sticky-terms-color, var(--primary));

            &:hover {
                cursor: pointer;
            }
        }

        .footer & {
            :deep(a) {
                color: inherit;
            }
        }
    }

    &__text-wrap {
        flex: 1; // Equals out the width of terms-elements
        margin-bottom: 0;
    }

    &--long {
        align-items: flex-start;

        .offer-terms__logo {
            margin-top: 2px;
            margin-right: 5px;
            float: left;

            @media (min-width: $screen-sm) {
                margin-top: 5px;
            }
        }

        .offer-terms__text {
            display: flex;
            flex: 1 1 0px;
            flex-direction: row;
            opacity: 1;
            gap: 15px;
        }

        p,
        li,
        .offer-terms__text-wrap {
            margin-bottom: 6px;
        }
    }

    &--subtle {
        .offer-terms__text {
            opacity: 0.7;
        }
    }

    &--sticky {
        position: sticky;
        margin-top: 15px;
        padding-top: 15px;
        background: linear-gradient(
            to bottom,
            transparent 0,
            var(--sticky-terms-background, var(--background)) 15px,
            var(--sticky-terms-background, var(--background)) 100%
        );
        transition: all 0.3s ease-out;
    }
}
</style>
